import { history } from "./history";
import axios from "axios";
import { userService } from "../user/services/user.service";
import stringUtils from "./string-utils";
import { IGNORED_REDIRECT_PATHS } from "../_components/PrivateRoute";

export const axiosPlatform = () =>
    supportPlatformResponse(
        supportErrorCodes(supportRefreshToken(supportTracing(supportAuthorization(prepareAxiosPlatform()))))
    );
export const axiosAuth = () =>
    supportPlatformResponse(
        supportErrorCodes(supportRefreshToken(supportTracing(supportAuthorization(prepareAxiosAuth()))))
    );
export const axiosBilling = () =>
    supportPlatformResponse(
        supportErrorCodes(supportRefreshToken(supportTracing(supportAuthorization(prepareAxiosBilling()))))
    );
export const axiosPaymentMatching = () =>
    supportPlatformResponse(
        supportErrorCodes(supportRefreshToken(supportTracing(supportAuthorization(prepareAxiosPaymentMatching()))))
    );

export const prepareAxiosAuth = () => {
    return prepareAxiosWithBaseURL(process.env.REACT_APP_API_AUTH_BASE_URL);
};

export const prepareAxiosPlatform = () => {
    return prepareAxiosWithBaseURL(process.env.REACT_APP_API_BASE_URL);
};

export const prepareAxiosBilling = () => {
    return prepareAxiosWithBaseURL(process.env.REACT_APP_API_BILLING_BASE_URL);
};

export const prepareAxiosPaymentMatching = () => {
    return prepareAxiosWithBaseURL(process.env.REACT_APP_API_PMS_BASE_URL);
};

export const prepareAxiosWithBaseURL = (baseURL) => {
    return axios.create({
        baseURL: baseURL,
    });
};

export const supportAuthorization = (instance) => {
    // Remove the static header setting
    // let storedUser = localStorage.getItem("user");
    // if (storedUser) {
    //     instance.defaults.headers.common["Authorization"] = "Bearer " + JSON.parse(storedUser).access_token;
    // }

    // Add a request interceptor to dynamically set the Authorization header
    instance.interceptors.request.use(
        (config) => {
            let storedUser = localStorage.getItem("user");
            if (storedUser) {
                const user = JSON.parse(storedUser);
                if (user.access_token) {
                    config.headers["Authorization"] = "Bearer " + user.access_token;
                }
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    return instance;
};

export const supportTracing = (instance) => {
    instance.defaults.headers.common["X-PATHWAY-FRONTEND-URL"] = window.location.href;
    return instance;
};

export const nullEmptyStrings = (instance) => {
    instance.interceptors.request.use(
        (request) => {
            if (request.data) {
                let keys = Object.keys(request.data);
                keys.forEach((key) => {
                    if (request.data[key] === "") {
                        request.data[key] = undefined;
                    }
                });
            }
            return request;
        },
        (error) => {
            // Do something with request error
            return Promise.reject(error);
        }
    );

    return instance;
};

// This function is used to interpret error codes at the login process
export const supportAuthErrorCodes = (instance) => {
    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            if (error.response) {
                // First check for a custom message by our backend
                if (error.response.headers && error.response.headers["pathway-custom-reason"]) {
                    return Promise.reject(error.response.headers["pathway-custom-reason"]);
                }

                // Then check the status
                if (error.response.status) {
                    if (error.response.status === 401 || error.response.status === 400) {
                        if (error.response.data && error.response.data["error_description"]) {
                            if (
                                stringUtils.equalsIgnoreCase(
                                    error.response.data["error_description"],
                                    "Bad credentials"
                                )
                            ) {
                                return Promise.reject("E-Mail oder Passwort falsch");
                            } else if (!stringUtils.isBlank(error.response.data["error_description"])) {
                                return Promise.reject(error.response.data["error_description"]);
                            }
                        }

                        return Promise.reject("Unauthorized");
                    } else if (error.response.status >= 500) {
                        return Promise.reject("Server Fehler bei der Autorisierung");
                    }
                }

                // Fallback to the error description if available
                if (error.response.data && !stringUtils.isBlank(error.response.data["error_description"])) {
                    return Promise.reject(error.response.data["error_description"]);
                }

                // Ultimate fallback when a response has been received by the server
                return Promise.reject(
                    "Es ist ein Fehler bei der Anmeldung aufgetreten. Bitte probiere es noch einmal."
                );
            }

            // When no response has been received, there usually is an error code, most likely a network error
            if (!stringUtils.isBlank(error.code)) {
                if (stringUtils.equalsIgnoreCase("ERR_NETWORK", error.code)) {
                    return Promise.reject(
                        "Der Server ist derzeit nicht erreichbar. Bitte versuche es später noch einmal"
                    );
                }

                return Promise.reject(error.request);
            }

            return Promise.reject(error.message);
        }
    );
    return instance;
};

export const supportErrorCodes = (instance) => {
    instance.interceptors.response.use(
        (response) => {
            return response;
        },
        (error) => {
            if (error.response) {
                // Redirection status codes
                let logoutStatuses = [401];
                if (error.response.status && logoutStatuses.indexOf(error.response.status) > -1) {
                    if (IGNORED_REDIRECT_PATHS.includes(window.location.pathname)) {
                        history.push("/login");
                    } else {
                        history.push("/login?redirect_url=" + window.location.pathname);
                    }
                }

                // Prio 1: check headers for custom reason
                if (error.response.headers && !stringUtils.isBlank(error.response.headers["reason"])) {
                    return Promise.reject(error.response.headers["reason"]);
                }

                // More...

                // Prio 2: interpret status codes
                if (error.response.status) {
                    if (error.response.status === 503) {
                        return Promise.reject(
                            "Der Server ist derzeit nicht erreichbar. Bitte versuche es später noch einmal"
                        );
                    }
                    // More...
                }
            }

            // Got no response, for example, when connection failed. There is potentially an axios error code
            if (!stringUtils.isBlank(error.code)) {
                if (stringUtils.equalsIgnoreCase("ERR_NETWORK", error.code)) {
                    return Promise.reject(
                        "Der Server ist derzeit nicht erreichbar. Bitte versuche es später noch einmal"
                    );
                }
            }

            if (!stringUtils.isBlank(error.message)) {
                return Promise.reject(error.message);
            }

            // Return a non-specific error
            return Promise.reject("Fehler beim Aufbauen der Verbindung");
        }
    );
    return instance;
};

export const forceJsonContentType = (instance) => {
    instance.interceptors.request.use(
        (config) => {
            config.headers["Content-Type"] = "application/json";
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );
    return instance;
};

export const supportRefreshToken = (instance) => {
    instance.interceptors.request.use(
        async (config) => {
            let user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
            if (user && user.expiration_time) {
                const currentTime = new Date().getTime();
                const expirationTime = new Date(user.expiration_time).getTime();
                const timeUntilExpiration = (expirationTime - currentTime) / 1000; // Convert to seconds

                // Refresh token if it expires in less than 10 minutes (600 seconds)
                if (timeUntilExpiration < 600) {
                    try {
                        await userService.useRefreshToken();
                    } catch (error) {
                        history.push("/login?redirect_url=" + window.location.pathname);
                        throw new axios.Cancel("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
                    }
                }
            }
            return config;
        },
        (error) => {
            // Do something with request error
            return Promise.reject(error);
        }
    );

    return instance;
};

export const supportPlatformResponse = (instance) => {
    instance.interceptors.response.use(
        (response) => {
            if (response) {
                try {
                    if (
                        response.data &&
                        response.data.errorMessage &&
                        response.data.errorMessage.length > 0 &&
                        (!response.data.response || response.data.response === "")
                    ) {
                        return Promise.reject(response.data.errorMessage);
                    } else if (
                        response.data.errorMessage !== "" &&
                        response.data.response &&
                        response.data.response !== ""
                    ) {
                        return Promise.resolve(response.data);
                    } else if (response.data.successMessage && response.data.successMessage !== "") {
                        return Promise.resolve(response.data);
                    } else if (response.data.warningMessage && response.data.warningMessage !== "") {
                        return Promise.resolve(response.data);
                    } else {
                        return Promise.resolve(response.data.response);
                    }
                } catch (error) {
                    return Promise.reject(error.message);
                }
            }
            return Promise.reject("Missing response");
        },
        (error) => {
            return Promise.reject(error);
        }
    );
    return instance;
};

// When an endpoint is able to return a warning message special checks are required to unwrap the warnings and response.
// As reducer should not make this distinction but only set received data this should be used to rewrap it so a user can accept {response: any, warningMessage: string}
// even though the warning message might not have been set and the result (received by the service) is the plain response object
export const prepareWarningResponseForSuccess = (result) => {
    if (result.warningMessage && result.response) {
        return {
            response: result.response,
            warningMessage: result.warningMessage,
        };
    } else if (result.errorMessage && result.response) {
        return {
            response: result.response,
            warningMessage: result.errorMessage,
        };
    } else if (result.response) {
        return { response: result.response, warningMessage: null };
    } else {
        return { response: result, warningMessage: null };
    }
};

export const appendParameters = (route, params) => {
    let keys = Object.keys(params).filter((key) => !!params[key] && params[key] !== "");
    if (keys.length > 0) {
        let first = keys.splice(0, 1);
        route += determineIncludingChar(route) + first + "=" + encodeURIComponent(params[first]);
        keys.forEach((key) => {
            route += "&" + key + "=" + encodeURIComponent(params[key]);
        });
    }
    return route;
};

const determineIncludingChar = (route) => {
    return route.includes("?") ? "&" : "?";
};
